import { observable, reaction, computed, action } from 'mobx';
import {
    RouterState,
    RouterStore as MobxStateRouterStore,
    HistoryAdapter,
    Route
} from 'mobx-state-router';
import { createHashHistory } from 'history';
import { events, emit } from '@estee/elc-service-bus';

export interface IStringMap {
    [key: string]: string;
}

export interface IRouteDefinition extends Route {
    location?: string;
    context?: string;
}

type HashType = 'slash' | 'hashbang' | 'noslash';

export class RouterStore {
    @observable private mobxStateStore: MobxStateRouterStore;
    private scrollReactionDisposer: Function;
    private adapter: HistoryAdapter;
    private isStarted: boolean = false;
    private routes: IRouteDefinition[];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(routes: IRouteDefinition[], notFound: string, rootStore?: any) {
        const actualRootStore = rootStore ? rootStore : this;
        this.routes = routes;
        this.mobxStateStore = new MobxStateRouterStore(
            actualRootStore,
            routes,
            new RouterState(notFound)
        );
    }

    public startRouting = (hashType: HashType = 'slash') => {
        if (this.isStarted) {
            return;
        }

        this.isStarted = true;
        this.registerRouteChangesHandler();
        const history = createHashHistory({
            hashType
        });
        this.adapter = new HistoryAdapter(this.mobxStateStore, history);
        this.adapter.observeRouterStateChanges();

        this.scrollReactionDisposer = reaction(
            () => this.currentRoute,
            () => {
                window.scrollTo(0, 0);
            }
        );
    };

    public get historyAdapter() {
        return this.adapter;
    }

    @computed
    public get queryParams() {
        return <IStringMap>this.mobxStateStore.routerState.queryParams;
    }

    @computed
    public get params() {
        return <IStringMap>this.mobxStateStore.routerState.params;
    }

    public destroy = () => {
        this.scrollReactionDisposer();
    };

    @computed
    public get currentRoute() {
        return this.mobxStateStore.getCurrentRoute().name;
    }

    @action
    public changeCurrentRoute = (
        newRoute: string,
        params: IStringMap = {},
        queryParams: IStringMap = {}
    ) => {
        const currentRoute = this.routes.find(route => route.name === this.currentRoute);
        const nextRoute = this.routes.find(route => route.name === newRoute);
        if (
            nextRoute &&
            currentRoute &&
            currentRoute.context !== nextRoute.context &&
            nextRoute.location
        ) {
            if (Object.keys(params).length > 0 && params.constructor === Object) {
                const paramsArr = Object.keys(params).map(key => params[key]);
                const path = paramsArr.reduce(
                    (acc, param) => `${acc}/${param}`,
                    nextRoute.location
                );
                this.changeLocation(path, queryParams);

                return;
            }
            this.changeLocation(nextRoute.location, queryParams);

            return;
        }
        void this.mobxStateStore.goTo(newRoute, params, queryParams);
    };

    @action
    public goBack = () => {
        this.adapter.goBack();
    };

    public get mobxStateRouter() {
        return this.mobxStateStore;
    }

    public changeLocation = (
        pathName: string,
        queryParams: IStringMap = {},
        enforceOrigin: boolean = false
    ) => {
        let encodedParams = this.uriEncodeParams(queryParams);
        encodedParams = encodedParams && `?${encodedParams}`;
        const pathWithParams = `${pathName}${encodedParams}`;

        if (!enforceOrigin && (pathName.indexOf('http') === 0 || pathName.indexOf('www') === 0)) {
            window.location.assign(pathWithParams);

            return;
        }
        const url = `${location.origin}${pathWithParams}`;
        window.location.assign(url);
    };

    public getUrlForRoute(
        routeName: string,
        params: IStringMap = {},
        queryParams: IStringMap = {}
    ) {
        const route = this.routes.find(routeCandidate => routeCandidate.name === routeName);
        if (!route) {
            return '/';
        }

        const href = this.adapter.history.createHref({
            search: this.uriEncodeParams(queryParams),
            pathname: this.getEncodedPath(route.pattern, params)
        });

        let path = route.location || '/';
        if (path.charAt(0) !== '/') {
            path = `/${path}`;
        }
        path = `${path}${href}`;

        return `${path}`;
    }

    public getExternalUrlForRoute(
        routeName: string,
        params: IStringMap = {},
        queryParams: IStringMap = {}
    ) {
        const origin = `${location.origin}`;
        const path = this.getUrlForRoute(routeName, params, queryParams);

        return `${origin}${path}`;
    }

    private registerRouteChangesHandler = () => {
        reaction(
            () => this.currentRoute,
            currentRoute => {
                const payload = {
                    currentRoute,
                    params: this.params,
                    queryParams: this.queryParams
                };
                emit(events.NAVIGATE, payload);
            }
        );
    };

    private getEncodedPath = (pattern: string, params: IStringMap) => {
        return pattern
            .split('/')
            .map<string>(partName => partName.trim())
            .map<string>(partName =>
                partName.charAt(0) === ':' ? params[partName.substr(1)] : partName
            )
            .join('/');
    };

    private uriEncodeParams = (params: IStringMap = {}) => {
        return Object.keys(params)
            .map(key => [key, params[key]].map(encodeURIComponent).join('='))
            .join('&');
    };
}
